import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedList, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Currency } from '@sorare/core/src/__generated__/globalTypes';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { RewardIcon } from '@sorare/core/src/atoms/rewards/Icon';
import { BodyS, LabelS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { AmountWithConversion } from '@sorare/core/src/components/buyActions/AmountWithConversion';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { RewardTooltip } from '@sorare/core/src/components/rewards/RewardTooltip';
import { Avatar } from '@sorare/core/src/components/user/Avatar';
import { MyClubLink } from '@sorare/core/src/components/user/MyClubLink';
import { UserName } from '@sorare/core/src/components/user/UserName';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { isType } from '@sorare/core/src/lib/gql';

import type {
  Bid_AnyRewardConfigInterface,
  Bid_AnyRewardInterface,
  Bid_tokenBid,
} from './__generated__/index.graphql.ts';

const Root = styled.div`
  background-color: var(--bid-background-color, var(--c-nd-100));
  padding: var(--intermediate-unit);

  &:first-of-type {
    border-top-left-radius: var(--double-unit);
    border-top-right-radius: var(--double-unit);
  }
  &:last-of-type {
    border-bottom-left-radius: var(--double-unit);
    border-bottom-right-radius: var(--double-unit);
  }
`;
const Content = styled.div`
  display: grid;
  align-items: center;
  column-gap: var(--double-unit);
  grid-template-columns: 32px auto;
`;

const Metas = styled.div`
  min-width: 0;
`;

type Props = {
  bid: Bid_tokenBid;
  displayAbsoluteDate: boolean;
  bidRewards?: Bid_AnyRewardInterface[];
  bidRewardConfigs?: Bid_AnyRewardConfigInterface[];
};

export const Bid = ({
  bid: { amounts, createdAt, bidder },
  displayAbsoluteDate,
  bidRewards,
  bidRewardConfigs,
}: Props) => {
  const { formatDate, formatDistanceToNow } = useIntlContext();

  if (!bidder || !isType(bidder, 'User')) return null;

  return (
    <Root>
      <Vertical>
        <SBHorizontal>
          <Content>
            <Avatar user={bidder} size={4} />
            <Metas>
              <BodyS>
                <MyClubLink user={bidder}>
                  <UserName user={bidder} />
                </MyClubLink>
              </BodyS>
              <LabelS color="var(--c-nd-600)">
                {displayAbsoluteDate
                  ? formatDate(createdAt, {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : formatDistanceToNow(createdAt)}
              </LabelS>
            </Metas>
          </Content>
          <div className="text-right">
            <AmountWithConversion
              primaryCurrency={Currency.ETH}
              hideExponent={false}
              monetaryAmount={amounts}
              column
            />
          </div>
        </SBHorizontal>
        {!!bidRewards && bidRewards.length > 0 && (
          <div>
            <Chip size="S">
              <FormattedMessage
                defaultMessage="This bid will be rewarded with {reward}"
                values={{
                  reward: (
                    <FormattedList
                      type="conjunction"
                      value={bidRewards.map(bidReward => (
                        <RewardTooltip reward={bidReward} key={bidReward.id}>
                          <RewardIcon reward={bidReward} />
                        </RewardTooltip>
                      ))}
                    />
                  ),
                }}
              />
            </Chip>
          </div>
        )}
        {!!bidRewardConfigs?.length && (
          <div>
            <Chip size="S">
              <FormattedMessage
                defaultMessage="This bid will be rewarded with {reward}"
                values={{
                  reward: (
                    <FormattedList
                      type="conjunction"
                      value={bidRewardConfigs.map(bidRewardConfig => (
                        <AnyRewardConfig
                          anyRewardConfig={bidRewardConfig}
                          variant={Variant.TEXT}
                          key={bidRewardConfig.id}
                        />
                      ))}
                    />
                  ),
                }}
              />
            </Chip>
          </div>
        )}
      </Vertical>
    </Root>
  );
};

Bid.fragments = {
  tokenBid: gql`
    fragment Bid_tokenBid on TokenBid {
      id
      amounts {
        ...AmountWithConversion_monetaryAmount
      }
      createdAt
      bidder: userBidder {
        slug
        ...Avatar_publicUserInfoInterface
        ...UserName_publicUserInfoInterface
        ...MyClubLink_publicUserInfoInterface
      }
    }
    ${AmountWithConversion.fragments.monetaryAmount}
    ${Avatar.fragments.publicUserInfoInterface}
    ${UserName.fragments.user}
    ${MyClubLink.fragments.user}
  ` as TypedDocumentNode<Bid_tokenBid>,
  AnyRewardInterface: gql`
    fragment Bid_AnyRewardInterface on AnyRewardInterface {
      id
      ...RewardIcon_AnyRewardInterface
      ...RewardTooltip_AnyRewardInterface
    }
    ${RewardIcon.fragments.AnyRewardInterface}
    ${RewardTooltip.fragments.AnyRewardInterface}
  ` as TypedDocumentNode<Bid_AnyRewardInterface>,
  AnyRewardConfigInterface: gql`
    fragment Bid_AnyRewardConfigInterface on AnyRewardConfigInterface {
      id
      ...AnyRewardConfig_anyRewardConfigInterface
    }
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<Bid_AnyRewardConfigInterface>,
};
